// Smoke Drive Internal / External share controls — London Smoke & Cure

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LinkOutlined, ShareAltOutlined, StopOutlined } from '@ant-design/icons';
import {
    createOrReuseDriveShare,
    DRIVE_SHARE_KIND,
    getDriveShareWebsiteUrl,
    listDriveSharesForItem,
    revokeDriveShare,
} from '../../services/operatorDriveShareService';
import { listEligibleRecipients } from '../../services/operatorChatService';
import { formatDriveModified } from '../../services/operatorDriveService';
import {
    DRIVE_SHARE_EXPIRY_PRESETS,
    expiryDateFromPreset,
} from '../../utils/operatorDriveShareGates';
import { placeOperatorDriveShareMenu } from '../../utils/operatorDriveShareMenuPlace';

async function copyText(text) {
    if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
    }
    window.prompt('Copy share link', text);
}

function shareActivityLine(share) {
    const bits = [];
    bits.push(share.kind === DRIVE_SHARE_KIND.INTERNAL ? 'Internal' : 'External');
    if (share.itemType === 'folder') bits.push('folder zip');
    if (share.passwordProtected) bits.push('password');
    if (share.expiresAt) bits.push(`expires ${formatDriveModified(share.expiresAt)}`);
    if (share.maxDownloads > 0) {
        bits.push(`${share.downloadCount || 0} / ${share.maxDownloads} downloads`);
    } else if (share.downloadCount) {
        bits.push(`${share.downloadCount} download${share.downloadCount === 1 ? '' : 's'}`);
    }
    if (share.lastDownloadedAt) {
        bits.push(`last ${formatDriveModified(share.lastDownloadedAt)}`);
    }
    if (share.memberNames?.length) {
        bits.push(`people: ${share.memberNames.join(', ')}`);
    }
    return bits.join(' · ');
}

/**
 * @param {{
 *   item: object,
 *   uid: string,
 *   userData: object,
 *   library?: object[],
 *   onStatus: (msg: string) => void,
 *   onError: (msg: string) => void,
 *   variant?: 'icon' | 'details',
 * }} props
 */
export default function OperatorDriveShareControls({
    item,
    uid,
    userData,
    library = [],
    onStatus,
    onError,
    variant = 'icon',
}) {
    const [menuOpen, setMenuOpen] = useState(false);
    const [busyKind, setBusyKind] = useState('');
    const [shares, setShares] = useState([]);
    const [expiryId, setExpiryId] = useState('never');
    const [maxDownloads, setMaxDownloads] = useState('');
    const [password, setPassword] = useState('');
    const [people, setPeople] = useState([]);
    const [pickedUids, setPickedUids] = useState(() => new Set());
    const shareButtonRef = useRef(null);
    const shareMenuRef = useRef(null);
    const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, visibility: 'hidden' });

    const itemId = item?.id;

    const updateShareMenuPlace = useCallback(() => {
        const button = shareButtonRef.current;
        if (!button) return;
        const rect = button.getBoundingClientRect();
        const menu = shareMenuRef.current;
        const placed = placeOperatorDriveShareMenu({
            buttonRect: rect,
            menuSize: menu
                ? { width: menu.offsetWidth, height: menu.offsetHeight }
                : null,
            viewport: { width: window.innerWidth, height: window.innerHeight },
        });
        setMenuStyle({ top: placed.top, left: placed.left, visibility: 'visible' });
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [itemId]);

    useLayoutEffect(() => {
        if (!menuOpen || variant !== 'icon') {
            setMenuStyle({ top: 0, left: 0, visibility: 'hidden' });
            return undefined;
        }
        updateShareMenuPlace();
        const onReposition = () => updateShareMenuPlace();
        window.addEventListener('resize', onReposition);
        window.addEventListener('scroll', onReposition, true);
        return () => {
            window.removeEventListener('resize', onReposition);
            window.removeEventListener('scroll', onReposition, true);
        };
    }, [menuOpen, updateShareMenuPlace, variant]);

    useEffect(() => {
        if (!menuOpen || variant !== 'icon') return undefined;
        const onPointerDown = (event) => {
            const target = event.target;
            if (shareButtonRef.current?.contains(target)) return;
            if (shareMenuRef.current?.contains(target)) return;
            setMenuOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [menuOpen, variant]);

    useEffect(() => {
        if (variant !== 'details' || !itemId) {
            setShares([]);
            return undefined;
        }
        let cancelled = false;
        listDriveSharesForItem(itemId)
            .then((list) => {
                if (!cancelled) setShares(list);
            })
            .catch(() => {
                if (!cancelled) setShares([]);
            });
        return () => {
            cancelled = true;
        };
    }, [variant, itemId]);

    useEffect(() => {
        if (variant !== 'details' || !uid) return undefined;
        let cancelled = false;
        listEligibleRecipients(uid)
            .then((list) => {
                if (!cancelled) {
                    setPeople(list.filter((p) => !p.isSelf));
                }
            })
            .catch(() => {
                if (!cancelled) setPeople([]);
            });
        return () => {
            cancelled = true;
        };
    }, [variant, uid]);

    const shareKind = async (kind, withForm) => {
        setBusyKind(kind);
        onError('');
        try {
            const members = withForm && kind === DRIVE_SHARE_KIND.INTERNAL
                ? people.filter((p) => pickedUids.has(p.uid))
                : [];
            const result = await createOrReuseDriveShare({
                itemId: item.id,
                kind,
                uid,
                userData,
                library,
                password: withForm && kind === DRIVE_SHARE_KIND.EXTERNAL ? password : '',
                expiresAt: withForm ? expiryDateFromPreset(expiryId) : null,
                maxDownloads: withForm ? Number(maxDownloads) || 0 : 0,
                memberUids: members.map((p) => p.uid),
                memberNames: members.map((p) => p.name),
            });
            await copyText(result.url);
            const label = kind === DRIVE_SHARE_KIND.INTERNAL ? 'Internal' : 'External';
            onStatus(
                result.reused
                    ? `${label} link copied.`
                    : `${label} link created and copied.`
            );
            setMenuOpen(false);
            setPassword('');
            if (variant === 'details') {
                const list = await listDriveSharesForItem(item.id);
                setShares(list);
            }
        } catch (err) {
            onError(err?.message || 'Could not create share link.');
        } finally {
            setBusyKind('');
        }
    };

    const handleRevoke = async (share) => {
        if (!window.confirm(`Revoke this ${share.kind} link? It will stop working at once.`)) {
            return;
        }
        onError('');
        try {
            await revokeDriveShare({ shareId: share.id, uid, userData });
            onStatus('Share link revoked.');
            setShares((prev) =>
                prev.map((s) => (s.id === share.id ? { ...s, revoked: true } : s))
            );
        } catch (err) {
            onError(err?.message || 'Could not revoke share.');
        }
    };

    const live = shares.filter((s) => !s.revoked);
    const togglePerson = (personUid) => {
        setPickedUids((prev) => {
            const next = new Set(prev);
            if (next.has(personUid)) next.delete(personUid);
            else next.add(personUid);
            return next;
        });
    };

    if (variant === 'details') {
        return (
            <div className="op-drive-share">
                <p className="op-drive-share__label">Share</p>
                <div className="op-drive-share__form">
                    <label className="op-drive-share__field">
                        <span>Expires</span>
                        <select
                            value={expiryId}
                            onChange={(e) => setExpiryId(e.target.value)}
                            aria-label="Share expiry"
                        >
                            {DRIVE_SHARE_EXPIRY_PRESETS.map((preset) => (
                                <option key={preset.id} value={preset.id}>
                                    {preset.label}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="op-drive-share__field">
                        <span>Download cap</span>
                        <input
                            type="number"
                            min="0"
                            placeholder="Unlimited"
                            value={maxDownloads}
                            onChange={(e) => setMaxDownloads(e.target.value)}
                            aria-label="Maximum downloads"
                        />
                    </label>
                    <label className="op-drive-share__field">
                        <span>External password</span>
                        <input
                            type="password"
                            autoComplete="new-password"
                            placeholder="Optional"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            aria-label="Optional external password"
                        />
                    </label>
                </div>
                {people.length > 0 ? (
                    <div className="op-drive-share__people">
                        <p className="op-drive-share__people-label">
                            Internal people (leave all off for any Admin / Production with the link)
                        </p>
                        <div className="op-drive-share__people-list">
                            {people.map((person) => (
                                <label key={person.uid} className="op-drive-share__person">
                                    <input
                                        type="checkbox"
                                        checked={pickedUids.has(person.uid)}
                                        onChange={() => togglePerson(person.uid)}
                                    />
                                    <span>{person.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                ) : null}
                <div className="op-drive-share__actions">
                    <button
                        type="button"
                        className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                        disabled={Boolean(busyKind)}
                        onClick={() => shareKind(DRIVE_SHARE_KIND.INTERNAL, true)}
                    >
                        <ShareAltOutlined />{' '}
                        {busyKind === DRIVE_SHARE_KIND.INTERNAL
                            ? 'Copying…'
                            : 'Internal (staff)'}
                    </button>
                    <button
                        type="button"
                        className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                        disabled={Boolean(busyKind)}
                        onClick={() => shareKind(DRIVE_SHARE_KIND.EXTERNAL, true)}
                    >
                        <ShareAltOutlined />{' '}
                        {busyKind === DRIVE_SHARE_KIND.EXTERNAL
                            ? 'Copying…'
                            : 'External (download page)'}
                    </button>
                </div>
                <p className="op-drive-share__hint">
                    Links stay on this website. Password is a page gate, not a lockbox. Folders
                    download as a zip. Storage URLs are not copied.
                </p>
                {live.length > 0 ? (
                    <ul className="op-drive-share__list">
                        {live.map((share) => (
                            <li key={share.id} className="op-drive-share__row">
                                <span className="op-drive-share__row-meta">
                                    {shareActivityLine(share)}
                                </span>
                                <button
                                    type="button"
                                    className="op-msg-link-btn"
                                    onClick={async () => {
                                        await copyText(getDriveShareWebsiteUrl(share.kind, share.id));
                                        onStatus('Share link copied.');
                                    }}
                                >
                                    Copy
                                </button>
                                <button
                                    type="button"
                                    className="op-msg-link-btn"
                                    onClick={() => handleRevoke(share)}
                                >
                                    <StopOutlined /> Revoke
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
        );
    }

    const shareMenu = menuOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
                ref={shareMenuRef}
                className="op-drive-share__menu op-drive-share__menu--portal"
                role="menu"
                style={{
                    top: menuStyle.top,
                    left: menuStyle.left,
                    visibility: menuStyle.visibility,
                }}
            >
                <button
                    type="button"
                    role="menuitem"
                    disabled={Boolean(busyKind)}
                    onClick={() => shareKind(DRIVE_SHARE_KIND.INTERNAL, false)}
                >
                    Internal — staff sign-in
                </button>
                <button
                    type="button"
                    role="menuitem"
                    disabled={Boolean(busyKind)}
                    onClick={() => shareKind(DRIVE_SHARE_KIND.EXTERNAL, false)}
                >
                    External — download page
                </button>
            </div>,
            document.body
        )
        : null;

    return (
        <div className="op-drive-share op-drive-share--icon">
            <button
                ref={shareButtonRef}
                type="button"
                className="op-msg-tool-btn"
                title="Share file"
                aria-label="Share"
                aria-expanded={menuOpen}
                onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen((v) => !v);
                }}
            >
                <LinkOutlined />
            </button>
            {shareMenu}
        </div>
    );
}
